/**
 * The outcome check - g.outcome(res, opts)
 *
 * The canonical FOUR-outcome envelope check as ONE call, for actions
 * OUTSIDE forms (former/validator already handle forms):
 *
 *   const res = await g.fetch('/app/thing/save', payload);
 *   if (!g.outcome(res, { shake: card, success: 'Saved.' })) return;
 *   // ... use res.data
 *
 * !res.success                  -> g.error(message)   [system failure]
 * res.error.code                -> g.warning(message) [business refusal, codes >= 1000]
 * res.data.validated === false  -> g.fieldError per opts.fields mapping +
 *                                  g.warning(data.message) [validation refusal]
 * otherwise                     -> g.success(opts.success) when given, silent otherwise
 *
 * Returns true ONLY on the clean-data outcome — a validation refusal is
 * false (vux #109: it used to pass as true, and callers cleared their
 * inputs over a refused write with nothing shown). opts.fields maps a
 * data.fields key to the element (or selector) its message belongs
 * under; unmapped keys reach the person through the warning toast
 * (data.message, or the first unmapped field's own text when the answer
 * carries no message). opts.shake rides the three failure toasts (the
 * notify shake option: glow follows the toast type); the success toast
 * never shakes.
 */
import { error, warning, success } from './notify.js';
import { translate } from './translate.js';
import { fieldError } from './field.js';

export function outcome(res, opts) {
	opts = opts || {};
	// a superseded latest-wins read (g.fetch opts.latest): nothing to show,
	// nothing to do - the newer call carries the answer
	if (res && res.superseded) return false;
	if (!res || !res.success) {
		error((res && res.error && res.error.message) || translate('Something went wrong. Please try again.'), { shake: opts.shake });
		return false;
	}
	if (res.error && res.error.code) {
		warning(res.error.message, { shake: opts.shake });
		return false;
	}
	if (res.data && res.data.validated === false) {
		const fields = res.data.fields || {};
		let unmapped = '';
		for (const name of Object.keys(fields)) {
			const target = opts.fields && opts.fields[name];
			const el = typeof target === 'string' ? document.querySelector(target) : target;
			if (el) fieldError(el, fields[name]);
			else if (!unmapped) unmapped = fields[name];
		}
		const message = res.data.message || unmapped;
		if (message) warning(message, { shake: opts.shake });
		return false;
	}
	if (opts.success) success(opts.success);
	return true;
}
